import { gql, useQuery, useMutation } from '@apollo/client'
import { flags, useFeatureFlags } from 'lib/feature-flags'
import {
  formatCreateNoteInput,
  formatUpdateNoteInput,
} from './session-note-utils'

const NOTES_DISABLED_ERROR = 'Coach notes are not enabled.'

function useCoachNotesEnabled() {
  const [isCoachNotesEnabled] = useFeatureFlags([flags.FLAG_FEAT_COACH_NOTES])
  return Boolean(isCoachNotesEnabled)
}

function assertCoachNotesEnabled(isCoachNotesEnabled) {
  if (!isCoachNotesEnabled) {
    return Promise.reject(new Error(NOTES_DISABLED_ERROR))
  }
  return null
}

async function runMutation(mutate, variables) {
  try {
    const result = await mutate({
      variables,
      errorPolicy: 'all',
    })

    if (result?.errors?.length) {
      throw new Error(result.errors[0].message || 'Unable to save note.')
    }

    if (!result?.data) {
      throw new Error('Unable to save note.')
    }

    return result.data
  } catch (err) {
    const graphQLError = err?.graphQLErrors?.[0]?.message
    const networkError = err?.networkError?.message
    throw new Error(
      graphQLError || networkError || err?.message || 'Unable to save note.',
    )
  }
}

const GET_NOTES = gql`
  query Notes($studentId: ID!) {
    notes(studentId: $studentId) {
      id
      authorable {
        id
        name
        email
      }
      title
      content
      notable {
        __typename
        ... on Student {
          id
        }
        ... on Session {
          id
          title
          startDateTime
        }
      }
      createdAt
      updatedAt
      lastViewedAt
      private
    }
  }
`

const CREATE_NOTE = gql`
  mutation CreateNote($input: CreateNoteInput!) {
    createNote(input: $input) {
      id
      title
      content
      createdAt
      lastViewedAt
      updatedAt
      private
      notable {
        __typename
        ... on Student {
          id
        }
        ... on Session {
          id
          title
          startDateTime
        }
      }
    }
  }
`

const UPDATE_NOTE = gql`
  mutation UpdateNote($input: UpdateNoteInput!) {
    updateNote(input: $input) {
      id
      title
      content
      lastViewedAt
      createdAt
      private
      updatedAt
      notable {
        __typename
        ... on Student {
          id
        }
        ... on Session {
          id
          title
          startDateTime
        }
      }
    }
  }
`

const DELETE_NOTE = gql`
  mutation DeleteNote($id: ID!) {
    deleteNote(id: $id) {
      message
      success
    }
  }
`

export function useGetNotes(studentId, options = {}) {
  const isCoachNotesEnabled = useCoachNotesEnabled()
  return useQuery(GET_NOTES, {
    variables: { studentId },
    fetchPolicy: 'no-cache',
    skip: !isCoachNotesEnabled || !studentId || options.skip,
  })
}

export function useCreateNote(refetchNotes) {
  const isCoachNotesEnabled = useCoachNotesEnabled()
  const [mutate, result] = useMutation(CREATE_NOTE, {
    onCompleted: () => refetchNotes?.(),
  })
  return [
    input =>
      assertCoachNotesEnabled(isCoachNotesEnabled) ||
      runMutation(mutate, { input: formatCreateNoteInput(input) }),
    result,
  ]
}

export function useUpdateNote(refetchNotes) {
  const isCoachNotesEnabled = useCoachNotesEnabled()
  const [mutate, result] = useMutation(UPDATE_NOTE, {
    onCompleted: () => refetchNotes?.(),
  })
  return [
    input =>
      assertCoachNotesEnabled(isCoachNotesEnabled) ||
      runMutation(mutate, { input: formatUpdateNoteInput(input) }),
    result,
  ]
}

export function useDeleteNote(refetchNotes) {
  const isCoachNotesEnabled = useCoachNotesEnabled()
  const [mutate, result] = useMutation(DELETE_NOTE, {
    onCompleted: () => refetchNotes?.(),
  })
  return [
    async id => {
      const disabled = assertCoachNotesEnabled(isCoachNotesEnabled)
      if (disabled) {
        return disabled
      }

      const data = await runMutation(mutate, { id: String(id) })
      const deleteResult = data?.deleteNote

      if (!deleteResult?.success) {
        throw new Error(deleteResult?.message || 'Unable to delete note.')
      }

      return deleteResult
    },
    result,
  ]
}
